import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Button, Image } from "react-bootstrap";
import {
  getDeactivatedUsersAPI,
  activateUserAPI,
} from "../../redux/features/adminSlice";
import dummyImg from "../../assets/images/dummy.png";
import CustomButton from "../CustomButton/CustomButton";
import DottedLoading from "../DottedLoading/DottedLoading";
import { format } from "date-fns";

export default function DeactivatedUserTable({ fetchApi }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!auth.token) {
        setError("Please log in to view deactivated users.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchApi(auth.token);
        if (response?.success) {
          setAllUsers(response.data || []);
        } else {
          throw new Error(response?.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error(
          error?.message || "Failed to fetch data. Please try again.",
          {
            position: "top-center",
          }
        );
        setError(error?.message || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [auth.token, fetchApi]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleActivateUser = async (userId) => {
    setIsSubmitting(true);
    try {
      await dispatch(activateUserAPI({ token: auth.token, userId })).unwrap();
      toast.success("User activated successfully!", { position: "top-center" });
      const response = await dispatch(
        getDeactivatedUsersAPI({ token: auth.token })
      ).unwrap();
      setAllUsers(response.data || []);
    } catch (error) {
      toast.error(error || "Failed to activate user.", {
        position: "top-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPages = Math.ceil((allUsers?.length || 0) / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentUsers = Array.isArray(allUsers)
    ? allUsers.slice(startIndex, endIndex)
    : [];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const getStatusBadge = (value, type = "status") => {
    const configs = {
      status: {
        pending_review: { label: "Pending Review", class: "status-pending" },
      },
      role: {
        instructor: { label: "Instructor", class: "status-active" },
        student: { label: "Student", class: "status-pending" },
        unknown: { label: "Unknown", class: "status-default" },
      },
    };

    const config = configs[type][value?.toLowerCase()] || {
      label: value || "Unknown",
      class: "status-default",
    };

    return (
      <span className={`status-badge ${config.class}`}>{config.label}</span>
    );
  };

  const isValidDate = (date) => {
    return date && !isNaN(new Date(date).getTime());
  };

  return (
    <>
      {isLoading && (
        <div className="loader-container">
          <div className="dottedLoading theme-color"></div>
        </div>
      )}
      <style jsx>{`
        .detail-grid {
          display: grid;
          grid-template-columns: repeat(1, 1fr);
          gap: 20px;
        }

        .detail-item {
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #f05203;
        }

        .detail-item.full-width {
          grid-column: 1 / -1;
        }

        .detail-label {
          font-size: 0.875rem;
          font-weight: 700;
          color: #1f1f1f;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 1rem;
          font-weight: 500;
          color: #1f2937;
          line-height: 1.5;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-pending {
          background: #fef3c7;
          color: #d97706;
        }

        .status-active {
          background: #d1fae5;
          color: #065f46;
        }

        .status-default {
          background: #f3f4f6;
          color: #6b7280;
        }

        .reason-box {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          font-size: 1rem;
          line-height: 1.7;
          min-height: 80px;
        }

        .detail-value a {
          color: #4299e1;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s ease;
        }

        .detail-value a:hover {
          color: #3182ce;
        }

        .detail-value a svg {
          margin-left: 8px;
          vertical-align: middle;
        }

        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="d-flex align-item-center justify-content-end d-sm-none">
        <label className="me-2 flex-shrink-0 align-self-center">
          No of Rows
        </label>
        <select
          className="form-control ms-16"
          value={rowsPerPage}
          onChange={handleRowsChange}
          style={{ width: "100px" }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="table-responsive mb-10">
        <table className="table student-table custom-table align-middle">
          <thead className="position-sticky top-0">
            <tr>
              <th className="ah-text-base" width="10%">
                Sr No.
              </th>
              <th className="ah-text-base" width="20%">
                Name
              </th>
              <th className="ah-text-base" width="20%">
                Email
              </th>
              <th className="ah-text-base" width="10%">
                Role
              </th>
              <th className="ah-text-base" width="20%">
                Deactivated By
              </th>
              <th className="ah-text-base" width="20%">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="6">Loading...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6">
                  <div className="alert alert-danger">Error: {error}</div>
                </td>
              </tr>
            ) : currentUsers.length > 0 ? (
              currentUsers.map((user, index) => (
                <tr key={user.userId}>
                  <td>
                    <div className="counter-block">
                      {startIndex + index + 1}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="img-holder me-15">
                        <Image
                          src={
                            process.env.REACT_APP_GALLERY_URL +
                            user?.profilePicture
                          }
                          alt={`Thumbnail ${index + 1}`}
                          className="img-fluid w-100 h-100"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = dummyImg;
                          }}
                        />
                      </div>
                      <strong className="ah-text-sm fw-semibold">
                        {user.fullName}
                      </strong>
                    </div>
                  </td>
                  <td className="fw-medium">{user.email}</td>
                  <td className="fw-medium">
                    {user.roleId === 2
                      ? "Instructor"
                      : user.roleId === 3
                      ? "Student"
                      : "Unknown"}
                  </td>
                  <td className="fw-medium">
                    {user.deactivatedBy?.userName || "N/A"}{" "}
                    <span className="text-muted ah-text-xs">
                      {user.deactivatedBy?.roleName
                        ? `(${user.deactivatedBy.roleName})`
                        : ""}
                    </span>
                  </td>
                  <td className="fw-medium">
                    <Button
                      variant="primary"
                      className="fw-semibold no-shadow py-7 ah-text-sm me-2"
                      onClick={() => handleViewDetails(user)}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">
                  <div className="alert alert-info">
                    No deactivated users found.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-center justify-content-sm-between align-items-center custom-pagination-holder">
        <div className="mx-auto">
          <button
            className="me-2 border-0 bg-transparent p-0"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg
              style={{ transform: "rotate(-90deg)" }}
              width="12"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              className={`bg-transparent border-0 ${
                currentPage === i + 1
                  ? "pagination-item active"
                  : "pagination-item"
              } me-2`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="me-2 border-0 bg-transparent p-0"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <svg
              style={{ transform: "rotate(90deg)" }}
              width="12"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="align-item-center d-none d-sm-flex">
          <label className="me-2 flex-shrink-0 align-self-center">
            No of Rows
          </label>
          <select
            className="form-control ms-16"
            value={rowsPerPage}
            onChange={handleRowsChange}
            style={{ width: "100px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Modal for viewing deactivation details */}
      <div
        className={`modal ${showModal ? "show" : ""}`}
        style={{
          display: showModal ? "block" : "none",
          background: "rgba(0,0,0,0.5)",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseModal();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Deactivated User Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {selectedUser && (
                <div className="detail-grid">
                  {/* <div className="detail-item">
                    <div className="detail-label">Name</div>
                    <div className="detail-value">{selectedUser.fullName || "N/A"}</div>
                  </div> */}
                  <div className="detail-item">
                    <div className="detail-label">Name</div>
                    <div className="detail-value">
                      {selectedUser?.fullName || "N/A"}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Email</div>
                    <div className="detail-value">
                      <a
                        href={`mailto:${selectedUser.email}`}
                        className="text-decoration-none"
                      >
                        <svg
                          className="ms-0 me-5"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                        </svg>
                        {selectedUser.email}
                      </a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Deactivation Date</div>
                    <div className="detail-value">
                      {isValidDate(selectedUser.startTime)
                        ? format(new Date(selectedUser.startTime), "MM-dd-yyyy")
                        : "Not Provided"}
                    </div>
                  </div>
                  <div className="detail-item full-width">
                    <div className="detail-label">Reason for Deactivation</div>
                    <div className="detail-value">
                      {selectedUser.reason}
                    </div>
                  </div>
                  <div className="detail-item full-width">
                    <div className="detail-label">Reactivation Request by student</div>
                    <div className="detail-value">
                      {selectedUser?.reactivationRequestDetails?.argument || "No reactivation request"}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                disabled={isSubmitting}
                onClick={() => {
                  handleActivateUser(selectedUser.userId);
                  setTimeout(() => {
                    handleCloseModal();
                  }, 1000);
                }}
              >
                {isSubmitting ? <DottedLoading /> : "Activate User"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}